/* eslint-disable no-unused-expressions */
import React, { useEffect, useRef, useState } from "react";

import styles from "./bottomSheetOverlay.module.scss";
import unlockBody from "@Utils/unLockBody";
import lockBody from "@Utils/lockBody";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { useHistory } from "hooks/useHistory";
import { useLocation } from "react-router";
import useModal from "@Components/Modal/useModal";
import wrapperHOC from "@Utils/wrapperHOC";

export interface Props {
  children?: any;
  hideBottomSheetOverlay: any;
  modalRef?: any;
  forceHide?: boolean;
  hideLine?: boolean;
  size?: "halfHeight" | "threeFourthHeight" | "fullHeight" | "fitContent" | "";
  className?: string;
  noBodyLock?: boolean;
  forceHideLine?: boolean;
  innerClassName?: string;
  noBlurBackground?: boolean;
  dragLineClass?: string;
  outerClickDisabled?: boolean;
  reducedZIndex?: boolean;
  isHistoryPush?: boolean;
  name?: string;
  linePosition?: "top" | "bottom";
  backdropClassName?: string;
}

const BottomSheetOverlay: React.FC<Props> = (props) => {
  const {
    modalRef,
    forceHide,
    hideLine,
    size,
    className,
    noBodyLock,
    forceHideLine,
    innerClassName,
    noBlurBackground,
    children,
    dragLineClass,
    outerClickDisabled,
    hideBottomSheetOverlay: prophideBottomSheetOverlay,
    reducedZIndex,
    isHistoryPush,
    name = "",
    linePosition = "top",
    backdropClassName,
  } = props;
  const [showOverlay, setShowOverLay] = useState(false);
  const dragTh = 30;
  const bottomsheetRef = useRef<any>(null);
  const diffPercentageRef = useRef<any>(null);
  const dragStartY = useRef<any>(null);
  const posRef = useRef<any>(null);
  const history = useHistory();
  const location = useLocation();

  const handleGoBack = (): void => {
    history.goBack();
  };

  const hideBottomSheetOverlay = (e: any): void => {
    setShowOverLay(false);
    !noBodyLock && !modalRef && unlockBody();
    setTimeout(() => {
      if (e && isHistoryPush) {
        handleGoBack(); // goBack() → popstate → handleOnHistoryPop → hideBottomSheetOverlay(null)
        return;
      }
      if (prophideBottomSheetOverlay) prophideBottomSheetOverlay();
    }, 300);
  };

  const handleOutsideClick = (): void => {
    if (!outerClickDisabled) {
      hideBottomSheetOverlay(true);
    }
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (isHistoryPush) useModal(history, location, name, hideBottomSheetOverlay);
  useEffect(() => {
    !noBodyLock && !modalRef && lockBody();
    setTimeout(() => {
      setShowOverLay(true);
    });

    // Ensure body is always unlocked when this component unmounts
    return () => {
      !noBodyLock && !modalRef && unlockBody();
    };
  }, []);

  useEffect(() => {
    if (forceHide) {
      hideBottomSheetOverlay(true);
    }
    return () => {
      !noBodyLock && !modalRef && unlockBody();
    };
  }, [forceHide]);

  const updatePosition = (clientY: any): void => {
    const slideDiff = clientY - dragStartY.current;
    const isPositiveDir = slideDiff > 0;

    if (bottomsheetRef.current) {
      const diffPer = (Math.abs(slideDiff) / bottomsheetRef.current.offsetWidth) * 100;

      if (isPositiveDir) {
        if (diffPer <= 100) {
          diffPercentageRef.current = diffPer;
          bottomsheetRef.current.style.transform = `translateY(${diffPer}%)`;
        }
      }
    }
  };

  const onTouchMove = (evt: any): void => {
    const touch = evt.targetTouches[0];
    const pos = touch.clientY - dragStartY.current;
    posRef.current = pos;
    if (bottomsheetRef.current) {
      bottomsheetRef.current.style.transitionProperty = "none";
    }

    updatePosition(touch.clientY);
  };

  const handleOnTouchStart = (e: any): void => {
    dragStartY.current = e.targetTouches[0].clientY;
    diffPercentageRef.current = 0;

    window.addEventListener("touchmove", onTouchMove);
  };

  const onDragEnd = (): void => {
    if (bottomsheetRef.current) {
      bottomsheetRef.current.style.transitionProperty = "transform";
    }
    if (posRef.current > 0 && diffPercentageRef.current > dragTh) {
      if (bottomsheetRef.current) {
        bottomsheetRef.current.style.transform = `translateY(100%)`;
      }
      setTimeout(() => {
        hideBottomSheetOverlay(true);
      }, 200);
    } else if (bottomsheetRef.current) {
      bottomsheetRef.current.style.transform = `translateY(0)`;
    }

    window.removeEventListener("touchmove", onTouchMove);
  };

  const getSize = (): any => {
    switch (size) {
      case "halfHeight":
        return styles.halfHeight;
      case "threeFourthHeight":
        return styles.threeFourthHeight;
      case "fullHeight":
        return styles.fullHeight;
      default:
        return "";
    }
  };

  return (
    <div
      className={formatClassNames(
        reducedZIndex ? styles.rzBottomSheetOverlay : styles.bottomSheetOverlay,
        className,
      )}
    >
      <div
        role="none"
        className={formatClassNames(
          styles.bottomSheetOuterContainer,
          showOverlay && styles.show,
          noBlurBackground && styles.noBlurBackground,
          backdropClassName,
        )}
        onClick={handleOutsideClick}
      />
      <div
        ref={bottomsheetRef}
        className={[
          styles.bottomSheetInnerContainer,
          showOverlay && styles.show,
          getSize(),
          innerClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* {!forceHideLine && linePosition === "top" && (
          <div
            className={formatClassNames(styles.dragLineWrap, dragLineClass)}
            onTouchStart={handleOnTouchStart}
            onTouchEnd={onDragEnd}
          >
            {!hideLine && <i className={styles.bottomSheetLine} />}
          </div>
        )} */}

        {children}

        {!forceHideLine &&
          forceHideLine &&
          linePosition === "bottom" && ( //  not showing drag line for m-web, d-web and app
            <div
              className={formatClassNames(
                styles.dragLineWrap,
                styles.bottomDragLineWrap,
                dragLineClass,
              )}
              onTouchStart={handleOnTouchStart}
              onTouchEnd={onDragEnd}
            >
              {!hideLine && (
                <i
                  className={formatClassNames(styles.bottomSheetLine, styles.bottomSheetLineDark)}
                />
              )}
            </div>
          )}
      </div>
    </div>
  );
};

BottomSheetOverlay.defaultProps = {
  modalRef: null,
  forceHide: false,
  hideLine: false,
  size: "",
  className: "",
  noBodyLock: false,
  forceHideLine: false,
  innerClassName: "",
  noBlurBackground: false,
  dragLineClass: "",
  outerClickDisabled: false,
  children: <></>,
  isHistoryPush: false,
  backdropClassName: "",
};

export default wrapperHOC(BottomSheetOverlay, {
  componentName: "BottomSheetOverlay-Global",
  showForChina: true,
});
